'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { copyToClipboard } from '../../lib/clipboard';
import {
  FONT,
  Field,
  Seg,
  SHARED_CSS,
  apportion,
  card,
  cta,
  label,
  money,
  num,
  ombreText,
  sendBtn,
} from '../calculators/shared';

// Interactive splitter for the /tips Airbnb post. Mounted from post content via
// the [[widget:airbnb-calculator]] block — see app/tips/[slug]/BlogPostContent.
//
// The model, in one place so the arithmetic is auditable:
//   commonPool   = total x (sum of checked common-space percentages, 0-20%)
//   commonEach   = commonPool / people
//   sleepingPool = total - commonPool
//   weight       = (bathroom ? 2 : 1) + (bed ? 2 : 1), halved if sharing a room
//   sleepingEach = sleepingPool x (weight / sum of all weights)
//   owed         = sleepingEach + commonEach  (+ fees / people, if fees given)
//
// Fees are excluded from `total` and split evenly — everyone triggers cleaning
// equally, so weighting them by bedroom quality would be wrong.
//
// Rendered as stacked cards rather than the spec's wide table: seven columns
// cannot fit the 680px blog column, let alone a phone, and the per-person card
// carries every input and output the table did.

type Friend = { bathroom: boolean; bed: boolean; sharing: boolean };

const COMMON_SPACES = [
  { key: 'living', label: 'Living area', pct: 0.1 },
  { key: 'kitchen', label: 'Kitchen', pct: 0.05 },
  { key: 'bathrooms', label: 'Common area bathrooms', pct: 0.05 },
] as const;

type CommonKey = (typeof COMMON_SPACES)[number]['key'];

const newFriend = (): Friend => ({ bathroom: false, bed: true, sharing: false });

// Real CSS rather than inline styles: the table needs a media query to stay
// legible at 375px, and inline styles can't carry one.
// Only this calculator's own table. Everything else — the segmented pills,
// the info bubble, the top row, the send buttons — is in SHARED_CSS.
//
// The Bed/Other column gets 1.3fr: "Other" is a longer word than "No", so three
// equal columns sized the pills to the narrowest content and clipped that one
// inside its own overflow:hidden.
const GRID_CSS = `
.abnb-grid{display:grid;grid-template-columns:48px 1fr 1.3fr 1fr minmax(54px,auto);
  gap:9px 10px;align-items:center}
@media (max-width:430px){
  .abnb-grid{grid-template-columns:40px 1fr 1.3fr 1fr minmax(44px,auto);gap:8px 6px}
}
`;

const AirbnbSplitCalculator: React.FC = () => {
  const [total, setTotal] = useState('');
  const [fees, setFees] = useState('');
  const [common, setCommon] = useState<Record<CommonKey, boolean>>({
    living: true,
    kitchen: true,
    bathrooms: false,
  });
  const [friends, setFriends] = useState<Friend[]>(() =>
    Array.from({ length: 4 }, newFriend)
  );
  const [link, setLink] = useState('');
  const [name, setName] = useState('');
  const [copied, setCopied] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const [canShare, setCanShare] = useState(false);
  const draftRef = useRef<HTMLTextAreaElement | null>(null);
  // The text the count field shows. Kept separate from the count itself so the
  // field can be empty mid-edit — forcing it back to a number on every
  // keystroke makes it impossible to clear and retype.
  const [peopleText, setPeopleText] = useState('4');

  // Headcount IS friends.length. Holding it in a second useState made the two
  // able to disagree, and updating one from inside the other's setter is what
  // let two taps in a single React batch land as one increment.
  const people = friends.length;

  const resize = (fs: Friend[], n: number) => {
    const c = Math.max(1, Math.min(16, n));
    return c <= fs.length
      ? fs.slice(0, c)
      : [...fs, ...Array.from({ length: c - fs.length }, newFriend)];
  };

  const stepPeople = (delta: number) =>
    setFriends((fs) => {
      const next = resize(fs, fs.length + delta);
      setPeopleText(String(next.length));
      return next;
    });

  const typePeople = (text: string) => {
    setPeopleText(text);
    const n = parseInt(text.replace(/[^0-9]/g, ''), 10);
    if (Number.isFinite(n) && n >= 1) setFriends((fs) => resize(fs, n));
  };

  const update = (i: number, patch: Partial<Friend>) =>
    setFriends((prev) => prev.map((f, j) => (j === i ? { ...f, ...patch } : f)));

  const calc = useMemo(() => {
    const totalN = num(total);
    const feesN = num(fees);
    const commonPct = COMMON_SPACES.reduce((s, c) => s + (common[c.key] ? c.pct : 0), 0);
    const commonPool = totalN * commonPct;
    const commonEach = people > 0 ? commonPool / people : 0;
    const sleepingPool = totalN - commonPool;
    const weights = friends.map(
      (f) => ((f.bathroom ? 2 : 1) + (f.bed ? 2 : 1)) / (f.sharing ? 2 : 1)
    );
    const sumW = weights.reduce((a, b) => a + b, 0);
    const feeEach = people > 0 ? feesN / people : 0;

    // Each component is apportioned against its own pool, so the breakdown line
    // and the headline figure both reconcile — no column is a dollar out.
    const sleep = apportion(
      friends.map((_, i) => (sumW > 0 ? sleepingPool * (weights[i] / sumW) : 0)),
      sleepingPool
    );
    const commonCol = apportion(friends.map(() => commonEach), commonPool);
    const feeCol = apportion(friends.map(() => feeEach), feesN);

    const rows = friends.map((f, i) => ({
      friend: f,
      sleeping: sleep[i],
      common: commonCol[i],
      fee: feeCol[i],
      owed: sleep[i] + commonCol[i],
      withFees: sleep[i] + commonCol[i] + feeCol[i],
    }));
    return { commonPct, commonEach, rows, hasFees: feesN > 0, totalN, feesN };
  }, [total, fees, people, common, friends]);

  const describe = (f: Friend) =>
    [
      f.bathroom ? 'Private bathroom' : 'No private bathroom',
      // "Other" is the right toggle label but too vague in a message going to
      // the group — they need to know what they'd actually be sleeping on.
      f.bed ? 'Bed' : 'Couch, futon or similar',
      f.sharing ? 'Sharing' : 'Not sharing',
    ].join(', ');

  const message = useMemo(() => {
    const place = name.trim() || 'the Airbnb';
    const checked = COMMON_SPACES.filter((c) => common[c.key]).map((c) => c.label.toLowerCase());
    const list =
      checked.length > 1
        ? `${checked.slice(0, -1).join(', ')} and ${checked[checked.length - 1]}`
        : checked[0];
    // The shared-space note rides on the end of the intro rather than standing
    // as its own paragraph — it's a qualifier on the options below, and a
    // one-line paragraph between two blank lines read as its own announcement.
    const lines = [
      `Here are the cost options for each spot in ${place} calculated using Tabster. Which option do you prefer so we can figure out who goes where?${
        checked.length ? ` There's a shared ${list}.` : ''
      }`,
      '',
    ];
    // Blank line between options: this gets pasted into a group chat, where a
    // solid block of lines is far harder to scan than spaced-out choices.
    calc.rows.forEach((r, i) => {
      if (i > 0) lines.push('');
      lines.push(
        `Spot ${i + 1}: ${describe(r.friend)}: ${money(calc.hasFees ? r.withFees : r.owed)}`
      );
    });
    if (link.trim()) lines.push('', link.trim());
    return lines.join('\n');
  }, [name, link, common, calc]);

  // The three headers that need explaining. Keyed rather than one flag so
  // opening a second closes the first — two bubbles at once in a 5-column grid
  // pushes the rows apart twice over.
  const INFO: Record<string, string> = {
    bed: '"Other" means a couch, futon or sleeping bag.',
    sharing:
      "Anyone sharing a room. Someone sleeping on a couch in a common room isn't technically sharing the couch, but this may also count as sharing, since they're the one without privacy.",
    // Template string, not JSX around a conditional: JSX collapses the newline
    // either side of an expression into a space, so with no fees entered the
    // sentence rendered as "...common space . This is based...".
    owes: `Each person's share also carries ${money(calc.commonEach)} of common space${
      calc.hasFees ? ` and ${money(calc.feesN / Math.max(1, people))} of fees` : ''
    }. This is based on the calculations above.`,
  };

  const InfoDot: React.FC<{ k: string }> = ({ k }) => (
    <button
      type="button"
      className="abnb-i"
      data-open={info === k ? '1' : '0'}
      aria-label={`What ${k === 'owes' ? 'Owes all-in' : k} means`}
      aria-expanded={info === k}
      onClick={() => setInfo((p) => (p === k ? null : k))}
    >
      i
    </button>
  );

  // null means "follow the calculator"; a string means the reader has typed
  // over it and owns the text until they reset. Recomputing on top of an edit
  // would throw away their words every time a toggle moved.
  const text = draft ?? message;

  // Grow to fit rather than scrolling inside a fixed box — the whole point of
  // showing the message is that you can read all of it before sending.
  useEffect(() => {
    const el = draftRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  // Feature-detected after mount, never during render: this page is
  // prerendered, and `navigator` doesn't exist on the server, so branching on
  // it in the markup would hydrate to a different tree than it rendered.
  useEffect(() => {
    setCanShare(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  }, []);

  const onShare = async () => {
    try {
      await navigator.share({ text });
    } catch {
      // Dismissing the sheet rejects with AbortError. That's the user saying
      // no, not a failure worth surfacing.
    }
  };

  const onCopy = async () => {
    const ok = await copyToClipboard(text);
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div style={{ margin: '28px 0 34px', fontFamily: FONT }}>
      <style>{SHARED_CSS}</style>
      <style>{GRID_CSS}</style>
      <div style={card}>
        {/* Same two assets and the same -px tuck as PageHeader, scaled down.
            Not a link: this sits mid-article on the site it would link to, and
            a stray tap while filling the form would lose the reader's inputs. */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '18px' }}>
          <img
            src="/final-logo.png"
            alt=""
            style={{ width: '24px', height: '24px', objectFit: 'contain', marginTop: '2px' }}
          />
          <img
            src="/tabster.png"
            alt="Tabster"
            style={{ width: '84px', height: 'auto', objectFit: 'contain', marginLeft: '-18px' }}
          />
        </div>

        <div className="abnb-top">
          <div>
            <label style={label}>Airbnb cost</label>
            {/* "2,400" read as a value already entered rather than a hint —
                a placeholder that looks like real data is worse than none. */}
            <Field value={total} onChange={setTotal} placeholder="0" numeric prefix="$" />
          </div>
          <div>
            <label style={label}>Cleaning &amp; fees</label>
            {/* "Leave blank if unknown" no longer fits a third of the row;
                the same thing is said by the label above the table instead. */}
            <Field value={fees} onChange={setFees} placeholder="0" numeric prefix="$" />
          </div>
          <div>
            <label className="abnb-people-l" style={label}>
              People
            </label>
            <div className="abnb-people">
              {/* Bare glyphs, no chrome — these are steppers, and giving them
                  the field's background made them read as text inputs. */}
              <button
                type="button"
                aria-label="One fewer person"
                onClick={() => stepPeople(-1)}
                className="abnb-step"
              >
                −
              </button>
              <Field
                value={peopleText}
                onChange={typePeople}
                numeric
                center
                // Snap back to the real count if the field was left empty or
                // out of range, so it can never sit showing something the
                // arithmetic below isn't using.
                onBlur={() => setPeopleText(String(people))}
              />
              <button
                type="button"
                aria-label="One more person"
                onClick={() => stepPeople(1)}
                className="abnb-step"
              >
                +
              </button>
            </div>
          </div>
        </div>

        <div
          style={{
            height: '1px',
            background: 'rgba(255,255,255,0.08)',
            margin: '18px 0 16px',
          }}
        />

        <label style={{ ...label, marginBottom: '10px' }}>Which common spaces are there?</label>
        {COMMON_SPACES.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setCommon((p) => ({ ...p, [c.key]: !p[c.key] }))}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: '100%',
              background: 'transparent',
              border: 'none',
              padding: '8px 0',
              cursor: 'pointer',
              fontFamily: FONT,
              textAlign: 'left',
            }}
          >
            <span
              style={{
                width: '20px',
                height: '20px',
                flex: 'none',
                borderRadius: '6px',
                // White rather than cyan: the ombre now marks the questions,
                // so a cyan checked state competed with the labels for the
                // same meaning.
                border: common[c.key]
                  ? '1px solid rgba(255,255,255,0.55)'
                  : '1px solid rgba(255,255,255,0.22)',
                background: common[c.key] ? 'rgba(255,255,255,0.18)' : 'transparent',
                color: '#fff',
                fontSize: '13px',
                lineHeight: '19px',
                textAlign: 'center',
              }}
            >
              {common[c.key] ? '✓' : ''}
            </span>
            <span style={{ fontSize: '13.5px', color: 'rgba(255,255,255,0.85)' }}>{c.label}</span>
            {/* A bare "10%" reads as a share of something unstated — of the
                bill, of the common-space pool, of one person's total. */}
            <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
              {Math.round(c.pct * 100)}% of cost
            </span>
          </button>
        ))}
        {/* No rule above: this is the result of the checkboxes it sits under,
            and a line between them read as a new section rather than the
            answer to that one. The rule goes below instead, closing the
            common-space question off from the table that follows. */}
        <div
          style={{
            marginTop: '10px',
            // Matches .abnb-h — this is a derived readout like the table's
            // column headers, not an input the reader has to act on.
            fontSize: '11px',
            color: 'rgba(255,255,255,0.7)',
          }}
        >
          Each person owes{' '}
          <strong style={{ color: 'white' }}>{money(calc.commonEach)}</strong> for common spaces
        </div>

        <div
          style={{
            height: '1px',
            background: 'rgba(255,255,255,0.08)',
            margin: '16px 0 16px',
          }}
        />

        <label style={{ ...label, marginBottom: '14px' }}>
          What accommodations are available in this Airbnb?
        </label>
        <div className="abnb-grid">
          <div className="abnb-h abnb-l">Spots</div>
          <div className="abnb-h">Own bathroom?</div>
          <div className="abnb-h">
            Bed or other (i.e. Couch)? <InfoDot k="bed" />
          </div>
          <div className="abnb-h">
            Sharing? <InfoDot k="sharing" />
          </div>
          <div className="abnb-h abnb-r">
            Owes all-in <InfoDot k="owes" />
          </div>

          {/* The bubble is a full-width grid row rather than a popover
              anchored to the icon. A ~60px column can't contain it, and
              absolutely positioning it out of one would need the overflow
              handling this side-steps entirely. */}
          {info && <div className="abnb-info">{INFO[info]}</div>}

          {friends.map((f, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className="abnb-rule" />}
              <div className="abnb-p">{i + 1}</div>
              <Seg value={f.bathroom} onChange={(v) => update(i, { bathroom: v })} yes="Yes" no="No" />
              <Seg value={f.bed} onChange={(v) => update(i, { bed: v })} yes="Bed" no="Other" />
              <Seg value={f.sharing} onChange={(v) => update(i, { sharing: v })} yes="Yes" no="No" />
              <div className="abnb-amt">
                {money(calc.hasFees ? calc.rows[i].withFees : calc.rows[i].owed)}
              </div>
            </React.Fragment>
          ))}
        </div>

        <div
          style={{
            height: '1px',
            background: 'rgba(255,255,255,0.08)',
            margin: '18px 0 16px',
          }}
        />

        <label style={label}>
          Send the details to your friends. Confirm who's going where before booking.
        </label>
        <div style={{ marginBottom: '10px' }}>
          <Field value={name} onChange={setName} placeholder="Airbnb name" />
        </div>
        <div style={{ marginBottom: '12px' }}>
          <Field value={link} onChange={setLink} placeholder="Paste the Airbnb link" />
        </div>
        <textarea
          ref={draftRef}
          value={text}
          onChange={(e) => setDraft(e.target.value)}
          spellCheck={false}
          aria-label="Message to send your friends"
          style={{
            display: 'block',
            width: '100%',
            boxSizing: 'border-box',
            resize: 'none',
            overflow: 'hidden',
            outline: 'none',
            background: 'rgba(0,0,0,0.30)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: '12px',
            padding: '14px',
            fontSize: '13.5px',
            lineHeight: 1.65,
            color: 'rgba(255,255,255,0.78)',
            fontFamily: FONT,
            marginBottom: '10px',
          }}
        />
        {/* Only offered once the two diverge — which covers both "I typed over
            it" and "I typed, then changed a toggle and the numbers below are
            now stale". */}
        {text !== message && (
          <button
            type="button"
            onClick={() => setDraft(null)}
            style={{
              display: 'block',
              marginLeft: 'auto',
              marginBottom: '10px',
              padding: 0,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: FONT,
              ...ombreText,
            }}
          >
            Reset to the calculated message
          </button>
        )}
        {/* Share is first in the DOM and reordered on desktop, so the mobile
            layout — where it takes the room and Copy shrinks to a glyph — needs
            no ordering exception. data-share gates that on Share existing: with
            no share sheet, a lone icon-only button would be a mystery. */}
        <div className="abnb-send" data-share={canShare ? '1' : '0'}>
          {canShare && (
            <button type="button" onClick={onShare} className="abnb-share" style={sendBtn}>
              <span style={ombreText}>Share</span>
            </button>
          )}
          <button
            type="button"
            onClick={onCopy}
            className="abnb-copy"
            style={sendBtn}
            aria-label={copied ? 'Copied' : 'Copy message'}
          >
            <span className="abnb-copy-full" style={ombreText}>
              {copied ? 'Copied ✓' : 'Copy message'}
            </span>
            {/* Solid, not ombre: background-clip:text paints glyphs, and an SVG
                stroke inside a transparent-filled span has nothing to paint. */}
            <span className="abnb-copy-icon" aria-hidden>
              {copied ? (
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#7fe9ff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7fe9ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
              )}
            </span>
          </button>
        </div>

        {/* Attached to the result rather than sitting further down the page —
            the moment someone has a number they're happy with is the moment
            the rest of the trip becomes the obvious next problem. Splitwise
            puts the same pitch on its calculator output for the same reason. */}
        <div
          style={{
            marginTop: '18px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            fontSize: '13px',
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.62)',
          }}
        >
          Going on a trip? Use Tabster to consolidate and divvy up trip expenses with the group.
          100% free on{' '}
          <a href="/" style={cta}>
            web
          </a>
          ,{' '}
          <a
            href="https://play.google.com/store/apps/details?id=ai.tabster.app"
            style={cta}
            target="_blank"
            rel="noopener"
          >
            Android
          </a>
          , and{' '}
          <a
            href="https://apps.apple.com/us/app/tabster-ai/id6737703202"
            style={cta}
            target="_blank"
            rel="noopener"
          >
            iOS
          </a>
          .
        </div>
      </div>
    </div>
  );
};

export default AirbnbSplitCalculator;
